import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { setSnackbar } from 'app/redux/slices/snackbarSlice';

// Shareable deep links into the project workspace.
//
// A permalink is the ordinary workspace URL plus one query param naming the
// item, so the view itself is unchanged: the workspace loads, the tab param
// picks the panel, and the panel opens its usual dialog on the linked item
// once the project data has arrived.
//
//   /site/<siteId>?tab=observations&observation=<id>
//   /site/<siteId>?tab=photos&photo=<id>
//   /site/<siteId>?tab=photos&video=<id>

export const OBSERVATION_PARAM = 'observation';
export const PHOTO_PARAM = 'photo';
export const VIDEO_PARAM = 'video';

export const ITEM_PARAMS = [OBSERVATION_PARAM, PHOTO_PARAM, VIDEO_PARAM];

export const observationPermalink = (siteId, observationId) =>
  `/site/${siteId}?tab=observations&${OBSERVATION_PARAM}=${observationId}`;

export const mediaPermalink = (siteId, id, isVideo) =>
  `/site/${siteId}?tab=photos&${isVideo ? VIDEO_PARAM : PHOTO_PARAM}=${id}`;

export const absoluteUrl = (path) => `${window.location.origin}${path}`;

// URLSearchParams updaters for setSearchParams(prev => ...).
export const withoutItemParams = (prev) => {
  const next = new URLSearchParams(prev);
  ITEM_PARAMS.forEach((key) => next.delete(key));
  return next;
};

const withMediaParam = (prev, id, isVideo) => {
  const next = withoutItemParams(prev);
  next.set(isVideo ? VIDEO_PARAM : PHOTO_PARAM, String(id));
  return next;
};

// Copies the absolute permalink for `path` and confirms via the snackbar.
export const useCopyLink = () => {
  const dispatch = useDispatch();
  return useCallback(
    async (path) => {
      const url = absoluteUrl(path);
      try {
        await navigator.clipboard.writeText(url);
        dispatch(setSnackbar({ status: 'success', message: 'Link copied' }));
      } catch {
        // Clipboard access is refused outside a secure context — show the
        // link so it can still be copied by hand.
        dispatch(setSnackbar({ status: 'info', message: url }));
      }
    },
    [dispatch],
  );
};

// Drives an ImageCarouselDialog from the ?photo= / ?video= params.
//
// `media` is the carousel's item list (photos and `isVideo` clips), in the
// same order the carousel receives it. When a linked item is present and the
// carousel is closed, `openAt(index)` is called once the media has loaded.
// Returns setters that mirror the open item into the URL so the address bar
// is itself a permalink.
export const useMediaPermalink = ({ media, loading, carouselOpen, openAt }) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const photoId = searchParams.get(PHOTO_PARAM);
  const videoId = searchParams.get(VIDEO_PARAM);

  useEffect(() => {
    // Skipped while open so paging through the carousel never resets it.
    if (carouselOpen || loading) return;
    if (photoId == null && videoId == null) return;
    const idx = media.findIndex((m) =>
      videoId != null
        ? m.isVideo && String(m.id) === videoId
        : !m.isVideo && String(m.id) === photoId,
    );
    if (idx >= 0) {
      openAt(idx);
      return;
    }
    // `media` is empty both before the first fetch resolves and for a project
    // with no media; only a populated list proves the link is stale.
    if (media.length === 0) return;
    dispatch(
      setSnackbar({
        status: 'error',
        message: 'That photo or video is no longer available.',
      }),
    );
    setSearchParams(withoutItemParams, { replace: true });
  }, [
    media,
    loading,
    carouselOpen,
    photoId,
    videoId,
    openAt,
    dispatch,
    setSearchParams,
  ]);

  const setMediaParam = useCallback(
    (item) =>
      setSearchParams((prev) => withMediaParam(prev, item.id, item.isVideo), {
        replace: true,
      }),
    [setSearchParams],
  );

  const clearItemParams = useCallback(
    () => setSearchParams(withoutItemParams, { replace: true }),
    [setSearchParams],
  );

  return { setMediaParam, clearItemParams };
};
